import React, { useCallback, useEffect, useState } from "react";
import { Box, Stack, Typography, Divider } from "@mui/material";
import {
  CancelRounded,
  CheckCircleOutlineRounded,
  Info,
} from "@mui/icons-material";

import "./cnh-card.css";

import { CNH } from "../../apis/user-related-services";
import { DadosCnh } from "../../models/cnh";

type CnhCardProps = {
  dataCnh?: DadosCnh;
  onClick?: () => void;
};

type Orientation = "vertical" | "horizontal";

function CnhCard({ dataCnh, onClick }: CnhCardProps) {
  const [orientation, setOrientation] = useState<Orientation>("vertical");

  useEffect(() => {
    function handleResize() {
      setOrientation(window.innerWidth > 550 ? "vertical" : "horizontal");
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const categoria = dataCnh?.categoriaAtual;
  const numeroCnh = dataCnh?.numeroCnh;
  const registro = dataCnh?.registro;
  const dataEmissao = dataCnh?.dataEmissao;
  const dataValidade = dataCnh?.dataValidadeCnh;

  const cnhVencida = dataCnh?.cnhVencida;
  const bloqueioBca = dataCnh?.bloqueioBca;
  const bloqueioCnhApreendida = dataCnh?.bloqueioCnhApreendida;
  const bloqueioDireto = dataCnh?.bloqueioDireto ?? false;

  // Converte DD/MM/YYYY HH:mm:ss para Date
  const parseData = (valor?: string | null): Date | null => {
    if (!valor) return null;

    const [parteData] = valor.split(" ");
    const partes = parteData.split("/");

    if (partes.length !== 3) return null;

    const [dia, mes, ano] = partes.map(Number);

    if (!dia || !mes || !ano) return null;

    const resultado = new Date(ano, mes - 1, dia);

    if (
      resultado.getFullYear() !== ano ||
      resultado.getMonth() !== mes - 1 ||
      resultado.getDate() !== dia
    ) {
      return null;
    }

    return resultado;
  };

  const validade = parseData(dataValidade);

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const cnhIsExpired =
    Boolean(cnhVencida) || (validade !== null && validade < hoje);

  const possuiRestricao = Boolean(
    bloqueioBca || bloqueioCnhApreendida || bloqueioDireto
  );

  const limiteDoisMeses = new Date(hoje);
  limiteDoisMeses.setMonth(limiteDoisMeses.getMonth() + 2);

  const cnhProximaVencimento =
    !cnhIsExpired && validade !== null && validade <= limiteDoisMeses;

  const statusCnh = () => {
    if (cnhIsExpired) return "Vencida";
    if (possuiRestricao) return "Possui restrição";
    if (cnhProximaVencimento) return "Próxima do vencimento";
    return "Regular";
  };

  const possuiAlerta = cnhIsExpired || possuiRestricao || cnhProximaVencimento;

  const cnhStatusText = cnhIsExpired
    ? "Sua CNH está vencida"
    : possuiRestricao
    ? "Sua CNH possui restrição"
    : cnhProximaVencimento
    ? "Sua CNH está próxima do vencimento"
    : "Sua CNH está regular";

  const alertBackgroundColor =
    cnhIsExpired || possuiRestricao
      ? "#EF4123"
      : cnhProximaVencimento
      ? "#ED9B16"
      : "#007932";

  const typhographyBackgroundColor = possuiAlerta ? "#000" : "#007932";

  const loadCnh = useCallback(() => {
    if (onClick) {
      onClick();
      return;
    }

    window.open("/app/app-minha-cnh", "_self");
  }, [onClick]);

  const iconeDeStatus = () => {
    if (cnhIsExpired || possuiRestricao) {
      return <CancelRounded color="error" />;
    }

    if (cnhProximaVencimento) {
      return <Info color="warning" />;
    }

    return <CheckCircleOutlineRounded color="success" />;
  };

  const formataData = (valor?: string | null) => {
    if (!valor) return "-";
    return valor.split(" ")[0];
  };

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      sx={{
        width: "100%",
        backgroundColor: "#fff",
        borderRadius: "0.275rem",
        cursor: "pointer",
        overflow: "hidden",
        boxShadow: "1px 2px 4px 0px #ABABAB1A",
        border: "1px solid #E0E3EB",
      }}
      onClick={loadCnh}
    >
      <Stack
        direction="row"
        justifyContent="center"
        sx={{
          width: "100%",
          backgroundColor: alertBackgroundColor,
          color: "#fff",
          py: 1,
        }}
      >
        <Typography fontWeight={700}>{cnhStatusText}</Typography>
      </Stack>

      <Stack direction="column" sx={{ width: "100%", p: 2 }}>
        <Stack direction="row" justifyContent="space-between">
          <Box
            sx={(theme) => ({
              [theme.breakpoints.down("sm")]: { width: "50%" },
            })}
          >
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              Situação
            </Typography>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                color: alertBackgroundColor,
                "& svg": {
                  width: "1rem",
                  marginRight: "0.25rem",
                },
              }}
            >
              {iconeDeStatus()}
              <Typography sx={{ fontWeight: 500 }} component="span">
                {statusCnh()}
              </Typography>
            </Typography>
          </Box>

          <Box
            textAlign="right"
            sx={(theme) => ({
              [theme.breakpoints.down("sm")]: { width: "50%" },
            })}
          >
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              Categoria
            </Typography>
            <Typography
              sx={{ color: typhographyBackgroundColor, fontWeight: 500 }}
            >
              {categoria ?? "-"}
            </Typography>
          </Box>
        </Stack>

        <Divider
          orientation={orientation}
          sx={{ my: 1, width: "100%", paddingX: "2rem" }}
        />

        <Stack direction="row" justifyContent="space-between">
          <Box
            sx={(theme) => ({
              [theme.breakpoints.down("sm")]: { width: "50%" },
            })}
          >
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              N&deg; da CNH
            </Typography>
            <Typography
              sx={{ color: typhographyBackgroundColor, fontWeight: 500 }}
            >
              {numeroCnh ?? "-"}
            </Typography>
          </Box>

          <Box
            textAlign="right"
            sx={(theme) => ({
              [theme.breakpoints.down("sm")]: { width: "50%" },
            })}
          >
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              Registro
            </Typography>
            <Typography
              sx={{ color: typhographyBackgroundColor, fontWeight: 500 }}
            >
              {registro ?? "-"}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 1, width: "100%", paddingX: "2rem" }} />

        <Stack direction="row" justifyContent="space-between">
          <Box
            sx={(theme) => ({
              [theme.breakpoints.down("sm")]: { width: "50%" },
            })}
          >
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              Emissão
            </Typography>
            <Typography
              sx={{ color: typhographyBackgroundColor, fontWeight: 500 }}
            >
              {formataData(dataEmissao)}
            </Typography>
          </Box>

          <Box
            textAlign="right"
            sx={(theme) => ({
              [theme.breakpoints.down("sm")]: { width: "50%" },
            })}
          >
            <Typography sx={{ color: "var(--xvia-color-text-secondary)" }}>
              Validade
            </Typography>
            <Typography
              sx={{ color: typhographyBackgroundColor, fontWeight: 500 }}
            >
              {formataData(dataValidade)}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default CnhCard;
